// A loyalty group's store locations, shared by the membership page's "Store locations" section and the group's full
// locations page (CIN-7192).
//
// Future: CIN-5497 (location required to redeem) checks a member's position against these same group locations; its
// portal side should use calculateDistance here rather than add another copy (LocationsPage still has its own).
// Known gap: distances are straight-line from the browser's position, and a store without coordinates has none.

const EARTH_RADIUS_MILES = 3959;

// Sorts a store without coordinates after every real distance. Never shown; see hasDistance.
const NO_COORDINATES_DISTANCE = 100000;

const toRadians = (degrees) => degrees * (Math.PI / 180);

/** Straight-line distance in miles between two points. */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return EARTH_RADIUS_MILES * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/** Whether a store carries a real distance: it has coordinates and the member shared their position. */
export const hasDistance = (location) => Boolean(location.lat && location.lon && location.distance);

/**
 * The stores nearest first once the member's position is known, with `distance` in miles; until then (or if they
 * decline), in the order the server sent them.
 *
 * @param {Array} locations - group.locations
 * @param {{lat: number, lon: number}|null} position - the member's position
 */
export const withDistances = (locations, position) => {
  if (!locations) return [];
  if (!position) return locations;
  return locations
    .map((location) => ({
      ...location,
      distance:
        location.lat && location.lon
          ? calculateDistance(position.lat, position.lon, location.lat, location.lon)
          : NO_COORDINATES_DISTANCE,
    }))
    .sort((a, b) => a.distance - b.distance);
};

/** Map bounds around every store with coordinates, padded by a tenth on each side; null when none has any. */
export const calculateMapBounds = (locations) => {
  const placed = (locations || []).filter((location) => location.lat && location.lon);
  // No coordinates at all: Math.min() of nothing is Infinity, which Leaflet rejects.
  if (placed.length === 0) return null;

  const lats = placed.map((location) => location.lat);
  const lons = placed.map((location) => location.lon);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLon = Math.min(...lons);
  const maxLon = Math.max(...lons);
  const latPadding = (maxLat - minLat) * 0.1;
  const lonPadding = (maxLon - minLon) * 0.1;

  return [
    [minLat - latPadding, minLon - lonPadding],
    [maxLat + latPadding, maxLon + lonPadding],
  ];
};

/**
 * The optional store-card rows any store fills. A row shows on every card or on none, which keeps the cards one size
 * without giving a program that has no phone numbers on file a blank line in each card.
 */
export const storeCardRows = (locations) => ({
  phone: locations.some((location) => location.phone),
  distance: locations.some(hasDistance),
});

/**
 * A store's phone number as members read it: "(801) 254-3307". Stores keep phones in E.164 ("+18012543307"), which
 * reads as a code, not a number. Anything that isn't a 10-digit North American number (an extension, a foreign
 * number) is shown as stored.
 */
export const formatPhone = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (national.length !== 10) return phone;
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
};

/** A Google Maps search for the store's address. */
export const googleMapsUrl = (location) => {
  const address = location.address || {};
  const query = [address.address1, address.address2, address.city, address.state, address.zip_code].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
};

/** Whether a store matches what the member typed: its name, street, city, state or ZIP. */
export const matchesSearch = (location, search) => {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  const address = location.address || {};
  return [location.friendly_name, location.name, address.address1, address.city, address.state, address.zip_code]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(needle));
};
